// ---------------------------------------------------------------------------
// What room is this, and how long does it last.
//
// One button in the header and one panel behind it. It answers the three
// questions a player in a room actually asks — which room am I in, who can get
// in, and why does it say the room is closing — and it is the only place any of
// them is answered outside the lobby's own header.
//
// **Read the header of RoomExpiry.tsx first.** The design of the countdown, both
// amendments to it, and the ONE RULE about `extend-room` are written down there,
// and this file is the half of that design that ended up being a popover. The
// reading itself (`levelFor`, `formatLeft`, `spellLeft`) lives in
// roomExpiryClock.ts and is imported rather than reimplemented, so the header
// button, the panel and the last-stretch strip cannot disagree about how long is
// left.
//
// WHY A POPOVER RATHER THAN A HEADER PILL. The pill was legible and unexplained:
// `1h58` beside a stopwatch says what is being measured and not what happens at
// zero, what the press does, or why a room has a lifetime. A sentence answers
// all three and does not fit in a header. So the header keeps a word and the
// sentence gets a panel.
//
// ALWAYS REACHABLE, AT EVERY LEVEL. The trigger is on screen for the whole time
// a player is in a room, including the ninety minutes when nothing is close to
// expiring and including a game in progress, when nothing is counting at all.
// That is deliberate and it is Isaac's: somebody idling a lobby who knows they
// are about to go quiet should be able to push the deadline back BEFORE being
// warned about it. A control that appears when the room is nearly gone is a
// control that arrives too late to be used calmly.
//
// DISMISSAL IS `AccountMenu`'s, NOT `HostRowMenu`'s, and the difference is the
// backdrop. A document-level `pointerdown` in the capture phase, Escape, and
// focus handed back to the trigger a task later. There is NO scrim, so there is
// no `swallowNextClick` either: that call exists for a press that lands on a
// backdrop covering a control, and with no backdrop "outside" is whatever the
// person was aiming at and pressing it should work (CLAUDE.md). Nothing here
// closes on `pointermove` or `pointerup`, for the reason written on
// `HostRowMenu` — the rule is the same even though this panel holds no slider.
//
// It is a popover at every width rather than a sheet on a phone. `HostRowMenu`
// is a sheet below 900px because it is anchored to a row in a list that scrolls;
// this hangs off the header, which does not move, so the geometry problem a
// sheet solves does not exist here. Nothing is pinned to the bottom of the
// screen, so `useVisualViewport` has nothing to say about it: the panel is
// anchored to the top of the window and scrolls its own contents.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import type { GameSettings } from '@countdown/shared';
import { Button } from '../primitives/Button.js';
import { CheckIcon, CopyIcon, GlobeIcon, Icon, LockIcon, TimerIcon } from '../primitives/Icon.js';
import {
  estimateDurationMs,
  formatDuration,
  groupedSequence,
  roundLabel,
  shareUrl,
} from '../../lib/format.js';
import { formatLeft, levelFor, spellLeft } from './roomExpiryClock.js';
import type { RoomExpiry } from './roomExpiryClock.js';

/**
 * The idle window, in words, as the copy says it.
 *
 * `IDLE_ROOM_TTL_MS` in `packages/backend/src/rooms.ts` is the number and it is
 * not importable from here — the backend is not a client dependency. It is
 * stated rather than fudged into "a while" because a room lifetime a player
 * cannot name is a room lifetime they cannot plan around, and the press gives
 * the sentence away immediately if it drifts: the reading jumps to the real
 * window in the same frame.
 */
const IDLE_WINDOW = 'two hours';

/**
 * Whether this viewer may be shown the code.
 *
 * `hideRoomCode` is the private half of streamer mode: the code is given out by
 * hand and must not be sitting on a screen for a stream to read off, and it is
 * hidden from everybody but the host. A panel that prints it anyway is that
 * setting defeated, so this is checked here as well as wherever the lobby checks
 * it.
 *
 * **The cast is deliberate and temporary.** `hideRoomCode` is arriving in
 * `packages/shared` as this is written and is not in that package's build yet,
 * so naming it on `GameSettings` would not compile against the shipped types.
 * Read defensively it does the right thing under both — absent reads as "not
 * hidden", which is what every room before the setting existed was. Delete the
 * cast and use `settings.hideRoomCode` directly once shared's build carries it.
 */
function codeIsHidden(settings: GameSettings, youAreHost: boolean): boolean {
  const hide = (settings as { hideRoomCode?: boolean }).hideRoomCode === true;
  return hide && !youAreHost;
}

export interface RoomInfoProps {
  roomCode: string;
  /** What the room is set to play. Read only here; the lobby is where it changes. */
  settings: GameSettings;
  /** Whether the code may be shown to this viewer. See `codeIsHidden`. */
  youAreHost: boolean;
  /** The room's single reading of its own deadline, from `useRoomExpiry`. */
  expiry: RoomExpiry;
  /** Sends `extend-room`. Called from an onClick and from nowhere else. */
  onExtend: () => void;
}

export function RoomInfoButton({
  roomCode,
  settings,
  youAreHost,
  expiry,
  onExtend,
}: RoomInfoProps): ReactElement {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  // Outside the close effect on purpose: closing is what tears that effect
  // down, and a timer cleared by its own cause never runs.
  const restoreRef = useRef(0);
  useEffect(() => () => window.clearTimeout(restoreRef.current), []);

  const { msLeft } = expiry;
  const level = levelFor(msLeft);
  // One reading, computed once, drawn on the button and in the panel. Null is
  // "nothing is counting", which is every phase of a game in progress.
  const reading = msLeft === null ? null : formatLeft(msLeft);

  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;

    // `pointerdown`, not `click`: a press that begins outside the panel already
    // means "not this panel", and waiting for the click leaves it open under a
    // finger that has started a scroll. Captured, so an item that stops
    // propagation cannot keep it alive.
    const onPointerDown = (event: PointerEvent) => {
      if (rootRef.current?.contains(event.target as Node) === true) return;
      setOpen(false);
      // Focus goes back a task later rather than here. The browser's own press
      // handling moves focus to whatever was pressed — to the body, when that
      // is nothing focusable — and it does that *after* this listener runs. It
      // goes back only if nothing else claimed it.
      restoreRef.current = window.setTimeout(() => {
        if (document.activeElement === document.body) triggerRef.current?.focus();
      }, 0);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close]);

  // Focus lands on the PANEL rather than on the first control in it, which is
  // where `AccountMenu` puts it and is the one thing not copied from there. The
  // first control in this panel can be `Keep it open` — a masked code takes the
  // copy button out — and dropping a keyboard user onto a control that changes
  // the room is one stray Enter away from extending it. The panel is a landing
  // spot, and Tab goes on to the controls from there.
  useEffect(() => {
    if (!open) return;
    panelRef.current?.focus();
  }, [open]);

  // The tick on Copy link, back to a copy mark after a couple of seconds. It is
  // armed by a press and by nothing else, so it starts no timer at mount and
  // adds nothing to the room's one deadline chain.
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2_000);
    return () => clearTimeout(timer);
  }, [copied]);

  const hidden = codeIsHidden(settings, youAreHost);
  const isPrivate = settings.visibility === 'private';
  const rounds = settings.roundSequence.length;
  const plan = groupedSequence(settings.roundSequence)
    .map((entry) => `${roundLabel(entry.type)[0]}${entry.count}`)
    .join(' ');

  const tone =
    level === 'urgent'
      ? 'border-alert/60 text-alert'
      : level === 'soon'
        ? 'border-accent/60 text-accent'
        : 'border-line text-ink-muted hover:border-line-strong hover:text-ink';
  const readingTone =
    level === 'urgent' ? 'text-alert' : level === 'soon' ? 'text-accent' : 'text-ink';

  // One sentence, said the same way to a screen reader and to a mouse hovering.
  // The countdown is in it at every level, including the calm one where the
  // button itself shows no number: a reading that is only a colour is a reading
  // some people never get. The code is deliberately not in it — it is in the
  // panel, behind a press, where `hideRoomCode` can take it away.
  const description =
    msLeft === null ? 'Room details' : `Room details. It closes in ${spellLeft(msLeft)}`;

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={description}
        title={`${description}.`}
        onClick={() => (open ? close() : setOpen(true))}
        // A fixed width at every level, so the header does not shuffle when the
        // word gives way to the reading, nor as the reading narrows from minutes
        // to seconds.
        className={`flex min-h-11 w-[68px] shrink-0 cursor-pointer items-center justify-center gap-1 rounded-[10px] border font-mono text-[11px] font-semibold transition-colors duration-150 ${tone}`}
      >
        {level === 'soon' || level === 'urgent' ? (
          <>
            <Icon icon={TimerIcon} size={14} />
            <span aria-hidden="true" className="tabular w-[30px] text-left">
              {reading}
            </span>
          </>
        ) : (
          <span aria-hidden="true" className="tracking-[0.12em] uppercase">
            room
          </span>
        )}
      </button>

      {open ? (
        <div
          ref={panelRef}
          data-testid="room-info"
          tabIndex={-1}
          // `right-0`, and never wider than the viewport less the header's own
          // gutters, so the far edge of a phone is the one thing it cannot run
          // past. It scrolls rather than growing taller than the screen.
          className="absolute top-full right-0 z-30 mt-1 flex max-h-[calc(100dvh-4.5rem)] w-72 max-w-[calc(100vw-2rem)] flex-col overflow-y-auto rounded-card border border-line bg-surface p-1.5 outline-none"
        >
          <div className="px-3 pt-2 pb-2.5">
            <span className="label-mono">room</span>
            <div className="mt-1.5 flex items-center gap-2">
              {/* Masked as asterisks rather than blurred, the same way and for
                  the same reason the lobby masks it: a blur is a picture of the
                  code that a still frame can often read back, and it says there
                  is something here to look at. */}
              <span
                data-testid="room-info-code"
                aria-hidden={hidden || undefined}
                className="font-mono text-[22px] leading-none font-bold tracking-[0.14em] text-blue-200"
              >
                {hidden ? '*'.repeat(roomCode.length) : roomCode}
              </span>
              {/* No copy button while the code is hidden: the link has the code
                  in it, so offering it would hand back exactly what the setting
                  took away. The host, who can see the code, still has one. */}
              {hidden ? (
                <span className="ml-auto text-[12px] text-ink-dim">host only</span>
              ) : (
                // The tick replaces the copy mark, which is the feedback the
                // changing label gives in the lobby. Same pattern, same words,
                // so the two surfaces read alike.
                <Button
                  size="sm"
                  variant="ghost"
                  className="ml-auto gap-1.5"
                  onClick={() => {
                    void navigator.clipboard?.writeText(shareUrl(roomCode));
                    setCopied(true);
                  }}
                >
                  <Icon
                    icon={copied ? CheckIcon : CopyIcon}
                    size={14}
                    className={copied ? 'text-good' : ''}
                  />
                  {copied ? 'Copied' : 'Copy link'}
                </Button>
              )}
            </div>
            <p className="mt-2.5 flex items-start gap-1.5 text-[13px] text-ink-muted">
              <Icon icon={isPrivate ? LockIcon : GlobeIcon} size={14} className="mt-[3px]" />
              <span>
                {isPrivate
                  ? 'Private. Only people with the code can find it.'
                  : 'Public. Anybody can find it in the room list.'}
              </span>
            </p>
          </div>

          <div aria-hidden="true" className="mx-2 h-px bg-line" />

          <div className="px-3 pt-2 pb-2.5">
            <span className="label-mono">game</span>
            <p className="mt-1.5 text-[13px] text-ink">
              {rounds === 1 ? '1 round' : `${rounds} rounds`},{' '}
              {formatDuration(estimateDurationMs(settings))}
            </p>
            {/* The mix, in the compact form every list in this app uses for a
                sequence too long to draw one chip per round: `L3 N1 C1`. Only
                the lobby's builder shows the plan in full. */}
            <p className="mt-1 font-mono text-[11px] text-ink-dim">{plan}</p>
          </div>

          <div aria-hidden="true" className="mx-2 h-px bg-line" />

          <div className="px-3 pt-2 pb-2">
            <span className="label-mono">closing</span>
            {reading === null ? (
              <p className="mt-1.5 text-[13px] text-ink-muted">
                Nothing is counting while a game is on. The {IDLE_WINDOW} start again when the
                game ends.
              </p>
            ) : (
              <>
                <p className="mt-1.5 text-[13px] text-ink">
                  <span className={`tabular font-mono font-bold ${readingTone}`}>
                    {reading}
                  </span>{' '}
                  left.
                </p>
                <p className="mt-1 text-[13px] text-ink-muted">
                  The room closes if nobody does anything for {IDLE_WINDOW}. A chat line counts, and
                  so does changing a setting.
                </p>
                {/* The only `extend-room` in this file, and it is an onClick.
                    See the ONE RULE on RoomExpiry.tsx.

                    It deliberately leaves the panel open. The reading two lines
                    up jumps to the full window in the same frame, and that jump
                    is the whole of the feedback this control ever had — closing
                    on the press would hide the one thing the press produces. */}
                <Button size="sm" variant="primary" block className="mt-2.5" onClick={onExtend}>
                  Keep it open
                </Button>
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
